/** @module api/energy_management_systems */

import { apiCall } from '@/api/index'
import { addQueryParamsToReqPayload } from '@/api/configure'
import {
  apiV1EnergyManagementSystemsPath,
  apiV1EnergyManagementSystemPath,
  relationshipGroupsApiV1EnergyManagementSystemPath,
  accessorsApiV1EnergyManagementSystemPath,
  possessorsApiV1EnergyManagementSystemPath,
  addToGroupsApiV1EnergyManagementSystemPath,
  removeFromGroupsApiV1EnergyManagementSystemPath
} from '@/../lib/routes'

/**
 * API GET to get a collection of EnergyManagementSystem(s)
 *
 * @function
 *
 * @param {object} params
 * @param {string} params.ctxt is the context of the request
 * @param {string} params.fields (optional) is a list of resource fields (attributes) to be included in the returned collection
 * @param {object} params.filter (optional) is a filter to by applied to the resource query
 * @param {string} params.sort (optional) is a sort-string (resource attribute/field name) the returned collection should be sorted by.
 *
 * @return {promise}
 */
export function getEnergyManagementSystems({ ctxt = 'admin', fields = [], filter = {}, sort = '' } = {}) {
  const payload = {}
  addQueryParamsToReqPayload(payload, 'energy_management_systems', { fields, filter, sort })

  return apiCall({
    path: apiV1EnergyManagementSystemsPath(ctxt),
    payload
  })
}

/**
 * API GET  to get a EnergyManagementSystem
 *
 * @function
 *
 * @param {object} params
 * @param {integer|string} params.id has to be the EMS's ID
 * @param {string} params.ctxt is the context of the request
 * @param {string} params.fields (optional) is a list of resource fields (attributes) to be included in the returned collection
 *
 * @return {promise}
 */
export function getEnergyManagementSystem({ id, ctxt = 'admin', fields = [] }) {
  const payload = {}
  addQueryParamsToReqPayload(payload, 'energy_management_systems', { fields })

  return apiCall({
    path: apiV1EnergyManagementSystemPath(ctxt, id),
    payload
  })
}

/**
 * API GET to get Groups connected to an EnergyManagementSystem
 *
 * @function
 *
 * @param {object} params
 * @param {integer|string} params.id has to be the EMS's ID
 * @param {string} params.ctxt is the context of the request
 * @param {object} params.membership (optional) supports to specify the membership, which builds the User .-< EMSs relation. Supported are `'Groups::OrganizationGroup'`, `'Groups::EmsAccessGroup'`, and `''`. If empty, all EMSs are returned.
 *
 * @return {promise}
 */
export function getGroupsForEms({ id, ctxt = 'admin', membership = 'Groups::OrganizationGroup' }) {
  const payload = {}
  const filter = {}
  if (membership) {
    filter.type = membership
  }
  addQueryParamsToReqPayload(payload, 'groups', { filter })

  return apiCall({
    path: relationshipGroupsApiV1EnergyManagementSystemPath(ctxt, id),
    payload
  })
}

/**
 * API GET to get Users connected to EMS through organizational groups
 *
 * @function
 *
 * @param {object} params
 * @param {integer|string} params.id has to be the EMS's ID
 * @param {string} params.ctxt is the context of the request
 *
 * @return {promise}
 */
export function getUsersForEmsThroughGroups({ id, ctxt = 'admin' }) {
  const payload = {}
  const filter = {
    type: 'Groups::OrganizationGroup'
  }
  addQueryParamsToReqPayload(payload, 'groups', { filter })

  return apiCall({
    path: accessorsApiV1EnergyManagementSystemPath(ctxt, id),
    payload
  })
}

/**
 * API GET to get Users connected to EMS with permissions
 *
 * @function
 *
 * @param {object} params
 * @param {integer|string} params.id has to be the EMS's ID
 * @param {string} params.ctxt is the context of the request
 *
 * @return {promise}
 */
export function getUsersForEmsWithPermission({ id, permission = {}, ctxt = 'admin' }) {
  const payload = {}
  const filter = {
    permission
  }
  addQueryParamsToReqPayload(payload, 'groups', { filter })

  return apiCall({
    path: accessorsApiV1EnergyManagementSystemPath(ctxt, id),
    payload
  })
}

/**
 * API GET to get Users connected to EMS through organizational groups
 *
 * @function
 *
 * @param {object} params
 * @param {integer|string} params.id has to be the EMS's ID
 * @param {string} params.ctxt is the context of the request
 *
 * @return {promise}
 */
export function getUsersForEmsDirect({ id, ctxt = 'admin', fields = [] }) {
  const payload = {}
  const filter = {
    type: 'Groups::EmsAccessGroup'
  }
  addQueryParamsToReqPayload(payload, 'groups', { fields, filter })

  return apiCall({
    path: possessorsApiV1EnergyManagementSystemPath(ctxt, id),
    payload
  })
}

/**
 * API POST to add a user to a group.
 *
 * @function
 *
 * @param {object} params
 * @param {integer|string} params.id has to be the ems's ID
 * @param Array{integer|string} params.groupIds has to be the group's IDs
 * @param {string} params.ctxt is the context of the request (Currently only `admin` context is authorized by BE.)
 *
 * @return {promise}
 */
export function addEmsToGroup({ id, groupIds, ctxt = 'admin' }) {
  const payload = {
    data: []
  }

  for (const groupId of groupIds) {
    payload.data.push({
      type: 'groups',
      id: groupId
    })
  }

  return apiCall({
    method: 'POST',
    path: addToGroupsApiV1EnergyManagementSystemPath(ctxt, id),
    payload
  })
}

/**
 * API DELETE to remove a user to a group.
 *
 * @function
 *
 * @param {object} params
 * @param {integer|string} params.id has to be the ems's ID
 * @param Array{integer|string} params.groupIds has to be the group's IDs
 * @param {string} params.ctxt is the context of the request (Currently only `admin` context is authorized by BE.)
 *
 * @return {promise}
 */
export function removeEmsFromGroup({ id, groupIds, ctxt = 'admin' }) {
  const payload = {
    data: []
  }

  for (const groupId of groupIds) {
    payload.data.push({
      type: 'groups',
      id: groupId
    })
  }

  return apiCall({
    method: 'DELETE',
    path: removeFromGroupsApiV1EnergyManagementSystemPath(ctxt, id),
    payload
  })
}
